import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EVENT_TYPES, EventType, LeadCreation, Region, REGIONS } from 'shared/topchef.types'
import { authenticatedCountryStorageKey } from 'pages/App';


type LeadEditorProps  = {
  submitButtonLabel: string;
  formData: LeadCreation;
  onDataChange: (name: string, value: string) => void;
  onSubmit: (formData: LeadCreation) => void;
  country?: string;
};


const LeadEditor = ({submitButtonLabel, formData, onDataChange, onSubmit, country }: LeadEditorProps) => {
  const { t } = useTranslation();

  const [isFormReady, setIsFormReady] = useState<boolean>(false);

  const effectiveCountry = country ?? localStorage.getItem(authenticatedCountryStorageKey) ?? 'france';
  const isFrenchExperience = effectiveCountry.toLowerCase() === 'france';

  const isValidInternationalPhone = (value: string): boolean => {
    const trimmedValue = value.trim();

    if (trimmedValue === '') {
      return false;
    }

    if (!/^\+?[0-9\s\-()]+$/.test(trimmedValue)) {
      return false;
    }

    const digitsCount = trimmedValue.replace(/\D/g, '').length;
    return digitsCount >= 6 && digitsCount <= 15;
  };

  const validateForm = useCallback(() => {
    const isRegionValid = !isFrenchExperience || (formData.region !== '' && formData.region !== 'unknown');
    const hasValidPhoneNumber =
      isValidInternationalPhone(formData.contactMobilePhone) ||
      isValidInternationalPhone(formData.contactLandlinePhone);

    if (formData.type !== '' && 
      formData.targetDate !== '' && isInFuture(formData.targetDate) &&
      isRegionValid && 
      formData.guests !== 0 && formData.guests !== '' &&
      formData.contactEmailAddress !== '' && 
      formData.contactName !== '' && 
      formData.contactCompany !== '' && 
      formData.description !== '' &&
      hasValidPhoneNumber
      ) {
        setIsFormReady(true);
      }
      else {
        setIsFormReady(false);
      }
    }, [formData, isFrenchExperience]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const dataToSubmit = {
      ...formData,
    };
    
    // Set region to null if not French experience
    if (!isFrenchExperience) {
      dataToSubmit.region = null;
    }
    
    onSubmit(dataToSubmit);
  };

  const handleInputEventChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value} = event.target;
    onDataChange(name, value);
  };


  const handleTelInputEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const hasInvalidCharacters = /[^0-9\s\-()+]/.test(value);

    if (hasInvalidCharacters) {
      return;
    }

    const startsWithPlus = value.startsWith('+');
    const valueWithoutPlus = value.replace(/\+/g, '');
    const nextValue = startsWithPlus ? `+${valueWithoutPlus}` : valueWithoutPlus;

    if (nextValue.length <= 25) {
      onDataChange(name, nextValue);
    }
  };


  function isInFuture(input: string): Boolean {
    const now = new Date();
    const tomorrow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const date = new Date(input);
    const dateToCompare = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    return dateToCompare >= tomorrow;
  }

  function tomorrow(): string {
    const now = new Date();
    const tomorrow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const formattedTomorrow = new Date(tomorrow).toISOString().split("T")[0];
    return formattedTomorrow
  }

  return (
    
    <div className="container p-0">
          <div>
              <div id="event_details_form" className="col-md-12">
                  <h4 className="mb-3">{t('leadEditor.eventDetails')}</h4>

                  <form id="brief_details_form" onSubmit={handleSubmit}>
                      <div id="details_event_form" className="md-3">
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_event_date" className="form-label">{t('leadEditor.eventDate')}</label><span className="required-marker">*</span>
                                  <input id="brief_event_date" name="targetDate" data-testid="targetDate" type="date" min={tomorrow()} value={formData.targetDate} className="form-control" required onChange={handleInputEventChange} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="event_type" className="form-label">{t('leadEditor.eventType')}</label><span className="required-marker">*</span>
                                  <select id="event_type" className="form-select" required name="type" data-testid="type" onChange={handleInputEventChange}  size={1} value={formData.type!}>
                                    <option value="">{t('leadEditor.selectEventType')}</option>
                                      {EVENT_TYPES.map((eventType: EventType) => (
                                        <option key={eventType.value} value={eventType.value} >
                                          {t(eventType.label)}
                                        </option>
                                      ))}
                                  </select>
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_date_starting" className="form-label">{t('leadEditor.startTime')}</label>
                                  <input type="time" id="brief_date_starting" className="form-control" name="startTime" onChange={handleInputEventChange} value={formData.startTime} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="brief_date_ending" className="form-label">{t('leadEditor.endTime')}</label>
                                  <input type="time" id="brief_date_ending" className="form-control" name="endTime" onChange={handleInputEventChange} value={formData.endTime} />
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_guests" className="form-label">{t('leadEditor.guestsCount')}</label><span className="required-marker">*</span>
                                  <input type="number" id="brief_guests" data-testid="guests" className="form-control" placeholder={String(t('leadEditor.guestsPlaceholder'))} required name="guests" onChange={handleInputEventChange} value={formData.guests!} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="brief_budget" className="form-label">{t('leadEditor.totalBudget')}</label>
                                  <input type="number" id="brief_budget" data-testid="budget" min={0} className="form-control" placeholder={String(t('leadEditor.amountExVat'))} name="budget" onChange={handleInputEventChange} value={formData.budget!} />
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_zipcode" className="form-label">{t('leadEditor.city')}</label>
                                  <input type="text" id="brief_zipcode" data-testid="city" placeholder={String(t('leadEditor.desiredLocation'))} aria-label={String(t('leadEditor.desiredLocation'))} className="form-control" name="city" onChange={handleInputEventChange} value={formData.city}/>
                              </div>
                              <div className="col-md-6">
                                {isFrenchExperience && (
                                  <>
                                    <label htmlFor="region" className="form-label">{t('leadEditor.region')}</label>{isFrenchExperience && <span className="required-marker">*</span>}
                                    <select id="region" name="region" data-testid="region" className="form-select" aria-label={String(t('leadEditor.selectRegion'))} size={1} onChange={handleInputEventChange} value={formData.region!} autoComplete="off">
                                      <option value="">{t('leadEditor.selectRegion')}</option>
                                      {REGIONS.map((region: Region) => (
                                        <option key={region.value} value={region.value}>
                                          {region.label}
                                        </option>
                                      ))}
                                    </select>
                                  </>
                                )}
                              </div>
                          </div>
                          <div className="mb-3">
                              <label htmlFor="event_details_description" className="form-label">{t('leadEditor.eventDetailsDescription')}</label><span className="required-marker">*</span>
                              <textarea className="form-control" id="event_details_description" data-testid="description" rows={9} placeholder={String(t('leadEditor.descriptionPlaceholder'))} required name="description" onChange={handleInputEventChange} value={formData.description!}></textarea>
                          </div>                      
                      </div>
                      <div id="details_contact_form" className="p-3 bg-light mb-3 rounded">
                            <h5 className="mb-3">{t('leadEditor.clientInfo')}</h5>
                          <div className="row">
                              <div className="col-md-6">
                                <label htmlFor="brief_company" className="form-label">{t('leadEditor.company')}</label><span className="required-marker">*</span>
                                  <input type="text" id="brief_company" data-testid="contactCompany" className="form-control" placeholder={String(t('leadEditor.companyPlaceholder'))} required name="contactCompany" onChange={handleInputEventChange} value={formData.contactCompany} />
                                </div>
                                <div className="col-md-6">
                                <label htmlFor="brief_contact_name" className="form-label">{t('leadEditor.contactFullName')}</label><span className="required-marker">*</span>
                                  <input type="text" id="brief_contact_name" data-testid="contactName"  className="form-control" placeholder={String(t('leadEditor.namePlaceholder'))} required name="contactName" onChange={handleInputEventChange} value={formData.contactName} />
                              </div>
                          </div>
                          <div className="row my-3">
                              <div className="col-md-6">
                                <label htmlFor="brief_contact_mail" className="form-label">{t('leadEditor.contactEmail')}</label><span className="required-marker">*</span>
                                  <input  type="email" id="brief_contact_mail" data-testid="contactEmailAddress" className="form-control" placeholder={String(t('leadEditor.emailPlaceholder'))} required name="contactEmailAddress" onChange={handleInputEventChange} value={formData.contactEmailAddress} />
                              </div>
                              <div className="col-md-6">
                                <label htmlFor="brief_contact_tel" className="form-label">{t('leadEditor.mobilePhone')}</label>
                                  <input type="tel" id="brief_contact_tel" autoComplete="tel" data-testid="contactMobilePhone" className="form-control" pattern="^\+?[0-9\s\-\(\)]+$" maxLength={25} name="contactMobilePhone" onChange={handleTelInputEventChange} value={formData.contactMobilePhone} />
                              </div>
                          </div>
                          <div className="row">
                              <div className="col-md-6">
                                <label htmlFor="brief_contact_tel_fix" className="form-label">{t('leadEditor.landlinePhone')}</label>
                                  <input type="tel" id="brief_contact_tel_fix" autoComplete="tel" className="form-control" data-testid="contactLandlinePhone" pattern="^\+?[0-9\s\-\(\)]+$" maxLength={25} name="contactLandlinePhone" onChange={handleTelInputEventChange} value={formData.contactLandlinePhone} />
                              </div>
                          </div>
                      </div>
                      
                    <div className="text-center">
                        <button id="save_brief_details" data-testid="validate-lead" className="btn btn-primary" disabled={!isFormReady}>{submitButtonLabel}</button>
                        {!isFormReady && (
                          <p className="brief-details-form-hint mb-0">{t('leadEditor.requiredFieldsAndPhoneHint')}</p>
                        )}
                    </div>
                    
                  </form>
              </div>
              
          </div>
      </div>
  );

}

export default LeadEditor;